import { createSlice } from '@reduxjs/toolkit';

const initialState = { 
    testCaseId: null,
    selectedTestCase: null,
    selectedTestCaseKey: null,
    
    selectedEnvironment: null,
    selectedAIModel: null,
    mcpToolsList: [],
    environmentList: [],
    aiModelList: [],

    fileSuggestions: [],
    // Execution state
    executionRecordSteps: [],
    executionRecordPlan: null,
    selectedStep: null,
    selectedStepIndex: null,
    activeTab: 0,
    replayProgressMap: {},
    isReplayStarted: false,
    editStepMap: {},
    originalExecutionRecordSteps: [],
    // UI state
    addStepForId: null,
    editStepId: null,
    isAddStepAtEnd: false,
    // Variables
    variablesList: [],
    variablesListDescriptions: {},
    isUnsavedChanges: false,
    testCaseChanged: false,
    newTestCaseValue: "",

    // Execution Results
    executionResults: null,
    replayError: null,
};

export const replaySlice = createSlice({
    name: 'replay',
    initialState,
    reducers: {
        setAIEvaluation: (state, action) => {
            state.AIEvaluation = action.payload;
        },
        setTestCaseId: (state, action) => {
            state.testCaseId = action.payload;
        },
        setSelectedTestCase: (state, action) => {
            state.selectedTestCase = action.payload;
        },
        setSelectedTestCaseKey: (state, action) => {
            state.selectedTestCaseKey = action.payload;
        },
        setMCPToolsList: (state, action) => {
            state.mcpToolsList = action.payload;
        },
        setEnvironmentList: (state, action) => {
            state.environmentList = action.payload;
        },
        setSelectedEnvironment: (state, action) => {
            state.selectedEnvironment = action.payload;
        },
        setAIModelList: (state, action) => {
            state.aiModelList = action.payload;
        },
        setSelectedAIModel: (state, action) => {
            state.selectedAIModel = action.payload;
        },
        setFileSuggestions: (state, action) => {
            state.fileSuggestions = action.payload;
        },
        setIsStopOnError: (state, action) => {
            state.isStopOnError = action.payload;
        },
        setIsDirectExecution: (state, action) => {
            state.isDirectExecution = action.payload;
        },
        // Execution state actions
        setExecutionRecordSteps: (state, action) => {
            state.executionRecordSteps = action.payload;
        },
        setExecutionRecordPlan: (state, action) => {
            state.executionRecordPlan = action.payload;
        },
        setSelectedStep: (state, action) => {
            state.selectedStep = action.payload;
        },
        setSelectedStepIndex: (state, action) => {
            state.selectedStepIndex = action.payload;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        setReplayProgressMap: (state, action) => {
            state.replayProgressMap = action.payload;
        },
        updateReplayProgress: (state, action) => {
            const { stepIndex, status } = action.payload;
            state.replayProgressMap[stepIndex] = status;
        },
        setIsReplayStarted: (state, action) => {
            state.isReplayStarted = action.payload;
        },
        setEditStepMap: (state, action) => {
            state.editStepMap = action.payload;
        },
        updateEditStepMap: (state, action) => {
            const { stepIndex, isEdited, editMap } = action.payload;
            if (editMap) {
                // Full map replacement
                state.editStepMap = editMap;
            } else if (stepIndex !== null && stepIndex !== undefined) {
                // Single step update
                state.editStepMap[stepIndex] = isEdited;
            }
        },
        setOriginalExecutionRecordSteps: (state, action) => {
            state.originalExecutionRecordSteps = action.payload;
        },
        // UI state actions
        setAddStepForId: (state, action) => {
            state.addStepForId = action.payload;
        },
        setEditStepId: (state, action) => {
            state.editStepId = action.payload;
        },
        setIsAddStepAtEnd: (state, action) => {
            state.isAddStepAtEnd = action.payload;
        },
        // Variables actions
        setVariablesList: (state, action) => {
            state.variablesList = action.payload;
        },
        setVariablesListDescriptions: (state, action) => {
            state.variablesListDescriptions = action.payload;
        },
        setIsUnsavedChanges: (state, action) => {
            state.isUnsavedChanges = action.payload;
        },
        setTestCaseChanged: (state, action) => {
            state.testCaseChanged = action.payload;
        },
        setNewTestCaseValue: (state, action) => {
            state.newTestCaseValue = action.payload;
        },

        setExecutionResults: (state, action) => {
            state.executionResults = action.payload;
        },

        setReplayError: (state, action) => {
            state.replayError = action.payload;
        },

        // reset changes
        resetChanges: (state) => {
            return {
                ...state,
                executionRecordSteps: state.originalExecutionRecordSteps,
                executionRecordPlan: {
                    ...state.executionRecordPlan,
                    steps: state.originalExecutionRecordSteps,
                },
                variablesList: state.variablesList,
                variablesListDescriptions: state.variablesListDescriptions,
                editStepMap: {},
                executionResults: null,
                replayError: null,
                isUnsavedChanges: false,
            };
        },

        //Reset Execution State
        resetExecutionState: (state) => {
            return {
                ...initialState,
                selectedEnvironment: state.selectedEnvironment,
                selectedAIModel: state.selectedAIModel,
                mcpToolsList: state.mcpToolsList,
                environmentList: state.environmentList,
                aiModelList: state.aiModelList,
                variablesList: state.variablesList,
                variablesListDescriptions: state.variablesListDescriptions,
            };
        },

        // Reset actions
        resetAllState: (state) => {
            return initialState;
        },
    },
});

export const {
    setAIEvaluation,
    setTestCaseId,
    setSelectedTestCase,
    setSelectedTestCaseKey,
    setMCPToolsList,
    setEnvironmentList,
    setSelectedEnvironment,
    setAIModelList,
    setSelectedAIModel,
    setFileSuggestions,
    setIsStopOnError,
    setIsDirectExecution,
    setExecutionRecordSteps,
    setExecutionRecordPlan,
    setSelectedStep,
    setSelectedStepIndex,
    setActiveTab,
    setReplayProgressMap,
    updateReplayProgress,
    setIsReplayStarted,
    setEditStepMap,
    updateEditStepMap,
    setOriginalExecutionRecordSteps,
    setAddStepForId,
    setEditStepId,
    setIsAddStepAtEnd,
    setVariablesList,
    setVariablesListDescriptions,
    resetAllState,
    resetExecutionState,
    setIsUnsavedChanges,
    setExecutionResults,
    resetChanges,
    setTestCaseChanged,
    setNewTestCaseValue,
    setReplayError,
} = replaySlice.actions;

export default replaySlice.reducer;